import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, ImagePlus, Loader2, Newspaper, Trash2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Training", "Race Reports", "Travel", "Nutrition", "Interviews"];
const EMPTY = { title: "", category: "Training", author_name: "", publish_date: "", body: "", status: "draft", cover_file_id: "", cover_path: "" };

const inputCls =
  "w-full bg-[#FBF9F5]/60 border border-[#0D1117]/15 rounded-xl px-4 py-3 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300";

const toDateInput = (date) => {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export default function AdminArticles({ authed, handleError }) {
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/articles`, authed());
      setArticles(response.data);
    } catch (error) {
      handleError(error, "Could not load articles");
    }
  }, [authed, handleError]);

  useEffect(() => {
    load();
  }, [load]);

  const chooseCover = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const uploadCover = async () => {
    if (!coverFile) return null;
    const data = new FormData();
    data.append("cover", coverFile);
    const response = await axios.post(`${API}/admin/articles/cover`, data, authed());
    return response.data;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const cover = coverFile ? await uploadCover() : null;
      const payload = { ...form, ...(cover ? { cover_file_id: cover.file_id, cover_path: cover.cover_path } : {}) };
      await axios.post(`${API}/admin/articles`, payload, authed());
      toast.success(payload.status === "published" ? "Article published" : "Draft saved");
      setForm(EMPTY);
      setCoverFile(null);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      setCoverPreview("");
      await load();
    } catch (error) {
      handleError(error, error?.response?.data?.detail || "Could not save article");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (article, status) => {
    try {
      const payload = {
        title: article.title,
        category: article.category,
        author_name: article.author_name,
        publish_date: article.publish_date,
        body: article.body,
        status,
        cover_file_id: article.cover_file_id || "",
        cover_path: "",
      };
      await axios.put(`${API}/admin/articles/${article.id}`, payload, authed());
      toast.success(status === "published" ? "Article published" : "Article moved to draft");
      await load();
    } catch (error) {
      handleError(error, "Could not update article");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Archive this article? It will disappear from the public site.")) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`, authed());
      toast.success("Article archived");
      await load();
    } catch (error) {
      handleError(error, "Could not archive article");
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.1fr] gap-8" data-testid="admin-articles">
      <form onSubmit={submit} className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4">
        <h3 className="font-serif text-2xl text-[#0D1117] flex items-center gap-2"><Newspaper className="w-5 h-5 text-[#FF4E00]" /> Article Composer</h3>
        <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="admin-article-title" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mb-2" htmlFor="admin-article-category">Category</label>
            <select id="admin-article-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="admin-article-category">
              {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mb-2" htmlFor="admin-article-author">Author</label>
            <input id="admin-article-author" required placeholder="Author name" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className={inputCls} data-testid="admin-article-author" />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">Publish date</label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="admin-article-date" className={`${inputCls} flex items-center justify-between text-left`}>
                  <span>{form.publish_date || "Pick publish date"}</span>
                  <CalendarDays className="w-4 h-4 text-[#FF4E00]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#FFFFFF]" align="start">
                <Calendar
                  mode="single"
                  selected={form.publish_date ? new Date(`${form.publish_date}T00:00:00`) : undefined}
                  onSelect={(date) => setForm({ ...form, publish_date: toDateInput(date) })}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mb-2" htmlFor="admin-article-status">Status</label>
            <select id="admin-article-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="admin-article-status">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>
        <label className="block rounded-2xl border border-dashed border-[#0D1117]/20 hover:border-[#FF4E00]/50 p-5 cursor-pointer transition-colors" data-testid="admin-article-cover-dropzone">
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseCover} className="hidden" data-testid="admin-article-cover-input" />
          {coverPreview ? (
            <img src={coverPreview} alt="Article cover preview" className="w-full aspect-video object-cover rounded-xl" />
          ) : (
            <span className="flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] py-10">
              <ImagePlus className="w-4 h-4 text-[#FF4E00]" /> Upload cover image
            </span>
          )}
        </label>
        <textarea required rows={9} placeholder="Article body. Separate paragraphs with a blank line." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inputCls} resize-y`} data-testid="admin-article-body" />
        <button type="submit" disabled={busy} data-testid="admin-article-save" className="w-full inline-flex items-center justify-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 rounded-full transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Newspaper className="w-4 h-4" />} Save Article
        </button>
      </form>

      <div className="space-y-4" data-testid="admin-articles-list">
        {articles.map((article) => (
          <article key={article.id} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-5 flex gap-4" data-testid="admin-article-row">
            <div className="w-24 h-20 rounded-xl bg-[#F4F2EC] overflow-hidden shrink-0">
              {article.cover_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${article.cover_url}`} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#FF4E00]">{article.category} · {article.status}</p>
              <h4 className="font-serif text-lg text-[#0D1117] truncate mt-1">{article.title}</h4>
              <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#6B7280] mt-1">{article.author_name} · {article.publish_date}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <button type="button" onClick={() => setStatus(article, article.status === "published" ? "draft" : "published")} data-testid="admin-article-publish-toggle" className="font-mono text-[9px] uppercase tracking-[0.16em] border border-[#0D1117]/15 hover:border-[#FF4E00] hover:text-[#FF4E00] px-3 py-2 rounded-full transition-colors">
                  {article.status === "published" ? "Unpublish" : "Publish"}
                </button>
                <button type="button" onClick={() => remove(article.id)} data-testid="admin-article-delete" className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#FF4E00] px-3 py-2 inline-flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Archive
                </button>
              </div>
            </div>
          </article>
        ))}
        {articles.length === 0 && <p className="text-sm text-[#6B7280]">No articles yet. Create the first one from the composer.</p>}
      </div>
    </div>
  );
}
